import type { ModuleManifest, Tab } from "@playbookai/ui";

import { ADMIN_TAB_ID, adminTab } from "../admin/admin-tab.js";
import { manifestToTab } from "../modules/launcher-entries.js";
import { ADMIN_ROUTE, NOTIFICATIONS_ROUTE, SETTINGS_ROUTE } from "../routes.js";
import {
  NOTIFICATIONS_TAB_ID,
  notificationsTab,
} from "../notifications/notifications-tab.js";
import { SETTINGS_TAB_ID, settingsTab } from "../settings/settings-tab.js";
import { findTabForPath, type TabsState } from "./tabs.js";

const STORAGE_PREFIX = "playbookai.shell.tabs.v1";
const STORAGE_VERSION = 1;
const MAX_TABS = 100;
const MAX_ID_LENGTH = 256;
const MAX_LABEL_LENGTH = 256;
const MAX_ROUTE_LENGTH = 2048;

export interface TabSessionScope {
  readonly tenantId: string;
  readonly userId: string;
}

export interface TabSessionAccess {
  readonly canAdminister: boolean;
  readonly enabledModules: readonly ModuleManifest[];
}

export type TabSessionState = Pick<
  TabsState,
  "tabs" | "activeTabId" | "counters" | "lastRouteById"
>;

interface StoredTab {
  readonly id: string;
  readonly label: string;
  readonly route: string;
  readonly pinned?: boolean;
}

interface StoredTabSession {
  readonly version: 1;
  readonly tabs: readonly StoredTab[];
  readonly counters: Readonly<Record<string, number>>;
  readonly lastRouteById: Readonly<Record<string, string>>;
}

/** A stable, scope-specific key. Tab state must never cross users or tenants. */
export function tabSessionStorageKey(scope: TabSessionScope): string {
  return `${STORAGE_PREFIX}:${encodeURIComponent(scope.tenantId)}:${encodeURIComponent(scope.userId)}`;
}

/**
 * Restore a tab strip from sessionStorage and rehydrate its non-serialisable
 * presentation from the access the user has now. Invalid, inaccessible and
 * duplicate entries are discarded. The URL, not the snapshot, selects the
 * active tab after a refresh.
 */
export function readTabSession(
  scope: TabSessionScope,
  access: TabSessionAccess,
  currentPath: string,
): TabSessionState | null {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(tabSessionStorageKey(scope));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || parsed.version !== STORAGE_VERSION) return null;

    return sanitiseTabSessionState(
      {
        tabs: Array.isArray(parsed.tabs) ? parsed.tabs : [],
        counters: parsed.counters,
        lastRouteById: parsed.lastRouteById,
      },
      access,
      currentPath,
    );
  } catch {
    // Storage can be disabled and its contents can be corrupt or user-edited.
    // A bad checkpoint must never block the authenticated shell from loading.
    return null;
  }
}

/** Validate and reconstruct one candidate checkpoint against current access. */
function sanitiseTabSessionState(
  value: {
    readonly tabs: readonly unknown[];
    readonly counters: unknown;
    readonly lastRouteById: unknown;
  },
  access: TabSessionAccess,
  currentPath: string,
): TabSessionState {
  const tabs: Tab[] = [];
  const ids = new Set<string>();
  for (const candidate of value.tabs.slice(0, MAX_TABS)) {
    const restored = restoreTab(candidate, access);
    if (!restored || ids.has(restored.id)) continue;
    ids.add(restored.id);
    tabs.push(restored);
  }
  const counters = restoreCounters(value.counters, access);
  return {
    tabs,
    activeTabId: findTabForPath(tabs, currentPath)?.id ?? "",
    counters: raiseCountersPastRestoredIds(counters, tabs, access),
    lastRouteById: restoreRememberedRoutes(value.lastRouteById, tabs),
  };
}

function raiseCountersPastRestoredIds(
  counters: Readonly<Record<string, number>>,
  tabs: readonly Tab[],
  access: TabSessionAccess,
): Readonly<Record<string, number>> {
  const next = { ...counters };
  const kinds = [
    "launcher",
    ...access.enabledModules.map((module) => module.id),
  ];
  for (const kind of kinds) {
    const prefix = `${kind}-`;
    for (const tab of tabs) {
      if (!tab.id.startsWith(prefix)) continue;
      const suffix = tab.id.slice(prefix.length);
      if (!/^\d+$/u.test(suffix)) continue;
      const used = Number(suffix);
      if (Number.isSafeInteger(used) && used > (next[kind] ?? 0)) {
        next[kind] = used;
      }
    }
  }
  return next;
}

/** Persist only the bounded, serialisable tab-session fields. */
export function writeTabSession(
  scope: TabSessionScope,
  access: TabSessionAccess,
  state: TabSessionState,
): void {
  const storage = getSessionStorage();
  if (!storage) return;

  try {
    const tabs = state.tabs
      .slice(0, MAX_TABS)
      .map((tab) => serialiseTab(tab, access))
      .filter((tab): tab is StoredTab => tab !== null);
    const ids = new Set(tabs.map((tab) => tab.id));
    const payload: StoredTabSession = {
      version: STORAGE_VERSION,
      tabs,
      counters: serialiseCounters(state.counters, access),
      lastRouteById: Object.fromEntries(
        Object.entries(state.lastRouteById).filter(
          ([id, route]) => ids.has(id) && isRememberedRoute(route),
        ),
      ),
    };
    storage.setItem(tabSessionStorageKey(scope), JSON.stringify(payload));
  } catch {
    try {
      storage.removeItem(tabSessionStorageKey(scope));
    } catch {
      // Storage is unavailable. Continue without refresh restoration.
    }
    // Full/private/disabled sessionStorage is a recoverable degradation: tabs
    // still work for the current document, only refresh restoration is lost.
  }
}

export function clearTabSession(scope: TabSessionScope): void {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.removeItem(tabSessionStorageKey(scope));
  } catch {
    // Logout must continue even when browser storage is unavailable.
  }
}

function restoreTab(value: unknown, access: TabSessionAccess): Tab | null {
  if (!isStoredTab(value)) return null;
  const pinned = value.pinned === true ? { pinned: true as const } : {};

  if (
    access.canAdminister &&
    value.id === ADMIN_TAB_ID &&
    isRouteWithin(value.route, ADMIN_ROUTE)
  ) {
    return { ...adminTab(value.route), label: value.label, ...pinned };
  }
  if (
    value.id === SETTINGS_TAB_ID &&
    isRouteWithin(value.route, SETTINGS_ROUTE)
  ) {
    return { ...settingsTab(value.route), label: value.label, ...pinned };
  }
  if (
    value.id === NOTIFICATIONS_TAB_ID &&
    value.route === NOTIFICATIONS_ROUTE
  ) {
    return { ...notificationsTab(), label: value.label, ...pinned };
  }

  const launcherMatch = /^\/launcher\/([1-9]\d*)$/.exec(value.route);
  if (launcherMatch && value.id === `launcher-${launcherMatch[1]}`) {
    return {
      id: value.id,
      label: value.label,
      route: value.route,
      ...pinned,
    };
  }

  const module = access.enabledModules.find(
    (candidate) =>
      isRouteWithin(value.route, candidate.routeBase) &&
      value.id.startsWith(`${candidate.id}-`) &&
      value.id.length > candidate.id.length + 1,
  );
  if (!module) return null;
  return {
    ...manifestToTab(module, value.id.slice(module.id.length + 1)),
    id: value.id,
    label: value.label,
    route: value.route,
    ...pinned,
  };
}

function serialiseTab(tab: Tab, access: TabSessionAccess): StoredTab | null {
  return restoreTab(tab, access)
    ? {
        id: tab.id,
        label: tab.label,
        route: tab.route,
        ...(tab.pinned ? { pinned: true } : {}),
      }
    : null;
}

function restoreCounters(
  value: unknown,
  access: TabSessionAccess,
): Readonly<Record<string, number>> {
  if (!isRecord(value)) return {};
  const allowed = new Set([
    "launcher",
    ...access.enabledModules.map((module) => module.id),
  ]);
  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, number] =>
        allowed.has(entry[0]) && isCounter(entry[1]),
    ),
  );
}

function serialiseCounters(
  counters: Readonly<Record<string, number>>,
  access: TabSessionAccess,
): Readonly<Record<string, number>> {
  return restoreCounters(counters, access);
}

function restoreRememberedRoutes(
  value: unknown,
  tabs: readonly Tab[],
): Readonly<Record<string, string>> {
  if (!isRecord(value)) return {};
  const restored: Record<string, string> = {};
  for (const tab of tabs) {
    const route = value[tab.id];
    if (!isRememberedRoute(route)) continue;
    const path = route.split("?")[0] ?? route;
    if (findTabForPath(tabs, path)?.id === tab.id) restored[tab.id] = route;
  }
  return restored;
}

function isStoredTab(value: unknown): value is StoredTab {
  if (!isRecord(value)) return false;
  return (
    isBoundedString(value.id, MAX_ID_LENGTH) &&
    isBoundedString(value.label, MAX_LABEL_LENGTH) &&
    isPathRoute(value.route) &&
    (value.pinned === undefined || typeof value.pinned === "boolean")
  );
}

function isRouteWithin(route: string, base: string): boolean {
  return route === base || route.startsWith(`${base}/`);
}

function isRememberedRoute(value: unknown): value is string {
  if (typeof value !== "string" || value.length > MAX_ROUTE_LENGTH) {
    return false;
  }
  const path = value.split("?")[0] ?? value;
  return isPathRoute(path) && !value.includes("#");
}

function isPathRoute(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.startsWith("/") &&
    value.length <= MAX_ROUTE_LENGTH &&
    !value.includes("?") &&
    !value.includes("#") &&
    !/[\u0000-\u001f\u007f]/u.test(value)
  );
}

function isBoundedString(value: unknown, maxLength: number): value is string {
  return (
    typeof value === "string" && value.length > 0 && value.length <= maxLength
  );
}

function isCounter(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
